import { Component, Input, OnInit, OnDestroy } from '@angular/core';

@Component({
  selector: 'app-countdown-timer',
  templateUrl: './countdown-timer.component.html',
  styleUrls: ['./countdown-timer.component.scss']
})
export class CountdownTimerComponent implements OnInit, OnDestroy {
  @Input() endTime?: string | Date;
  @Input() createdDateTime?: string | Date;
  @Input() alignment: 'left' | 'center' | 'right' = 'center';

  @Input() usagePeriod?: number;

  days: number = 0;
  hours: number = 0;
  minutes: number = 0;
  seconds: number = 0;
  isExpired: boolean = false;

  totalDuration: number = 0;
  remainingTime: number = 0;
  progressPercentage: number = 0;

  private intervalId: any;

  ngOnInit(): void {
    if (this.usagePeriod !== undefined && this.usagePeriod !== null) {
      this.calculateFromUsagePeriod();
    } else {
      this.startTimer();
    }
  }

  private calculateFromUsagePeriod(): void {
    const totalMinutes = this.usagePeriod ?? 0;
    this.days = Math.floor(totalMinutes / (60 * 24));
    this.hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    this.minutes = totalMinutes % 60;
    this.seconds = 0;
    this.isExpired = totalMinutes <= 0;
  }

  private calculateTotalDuration(): void {
    const start = this.createdDateTime
      ? new Date(this.createdDateTime).getTime()
      : new Date().getTime();
    const end = new Date(this.endTime!).getTime();

    this.totalDuration = end - start;

    if (this.totalDuration <= 0) {
      this.totalDuration = 0;
    }
  }

  private startTimer(): void {
    this.calculateTotalDuration();
    this.updateTime();

    this.intervalId = setInterval(() => {
      this.updateTime();
    }, 1000);
  }

  private updateTime(): void {
    const now = new Date().getTime();
    const start = this.createdDateTime
      ? new Date(this.createdDateTime).getTime()
      : new Date().getTime();
    const end = new Date(this.endTime!).getTime();

    const timePassed = now - start;
    const timeRemaining = end - now;

    if (timePassed < 0) {
      this.progressPercentage = 0;
      this.isExpired = false;
      this.days = 0;
      this.hours = 0;
      this.minutes = 0;
      this.seconds = 0;
      this.remainingTime = Math.floor((end - start) / 1000);
      return;
    }

    if (timeRemaining <= 0) {
      this.isExpired = true;
      this.days = 0;
      this.hours = 0;
      this.minutes = 0;
      this.seconds = 0;
      this.remainingTime = 0;
      this.progressPercentage = 100;

      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
      return;
    }

    this.days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
    this.hours = Math.floor((timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    this.minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
    this.seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

    this.remainingTime = Math.floor(timeRemaining / 1000);

    const rawProgress = this.totalDuration > 0 ? (timePassed / this.totalDuration) * 100 : 0;

    this.progressPercentage = Math.min(100, Math.max(1, rawProgress));
  }

  private padZero(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }

  get formattedDays(): string {
    return this.padZero(this.days);
  }

  get formattedHours(): string {
    return this.padZero(this.hours);
  }

  get formattedMinutes(): string {
    return this.padZero(this.minutes);
  }

  get formattedSeconds(): string {
    return this.padZero(this.seconds);
  }

  get progressStyle(): string {
    return `${this.progressPercentage}`;
  }

  get remainingProgressPercentage(): number {
    return 100 - this.progressPercentage;
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
}
